import { useId, useState } from 'react';
import { ISSUE_TYPES, type ProjectSummary } from '@overlayqa/shared';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowUpRight01Icon, Clock01Icon } from '@hugeicons/core-free-icons';
import { getTypeVisual, IconifyGlyph } from '../shared/badges/TypeBadge';
import { SegmentedControl } from '../ui/SegmentedControl';
import { StatBars, type StatBarDatum } from '../dashboard/StatBars';
import './ProjectOverview.css';

type OverviewData = Pick<ProjectSummary, 'severityBreakdown' | 'statusBreakdown' | 'typeBreakdown' | 'issueAging'>;
type Breakdown = 'severity' | 'type' | 'age';
const TABS: { value: Breakdown; label: string }[] = [
  { value: 'severity', label: 'Severity' }, { value: 'type', label: 'Type' }, { value: 'age', label: 'Age' },
];
const STATUSES = [
  { key: 'open', label: 'Open' }, { key: 'in-progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' }, { key: 'verified', label: 'Verified' }, { key: 'closed', label: 'Closed' },
] as const;
const SEVERITIES = [
  { key: 'critical', label: 'Critical' }, { key: 'high', label: 'High' },
  { key: 'medium', label: 'Medium' }, { key: 'low', label: 'Low' },
] as const;
const AGES = [
  { key: 'lessThan1d', label: 'Less than 1 day' }, { key: 'from1to3d', label: '1–3 days' },
  { key: 'from3to7d', label: '3–7 days' }, { key: 'from7to14d', label: '7–14 days' },
  { key: 'over14d', label: '14 days or more' },
] as const;
const count = (value: number | undefined) => Number.isFinite(Number(value)) ? Math.max(0, Number(value)) : 0;
const issues = (value: number) => `${value} ${value === 1 ? 'issue' : 'issues'}`;

export function ProjectOverview({ data }: { data: OverviewData }) {
  const [breakdown, setBreakdown] = useState<Breakdown>('severity');
  const id = useId();
  const statuses = STATUSES.map(status => ({ ...status, value: count(data.statusBreakdown[status.key]) }));
  const total = statuses.reduce((sum, status) => sum + status.value, 0);
  // fetchProjectSummary (project-summary.service.ts) excludes ignored rows from
  // every breakdown and limits aging to open + in-progress via openStatuses.
  const active = count(data.statusBreakdown.open) + count(data.statusBreakdown['in-progress']);
  const agingTotal = AGES.reduce((sum, age) => sum + count(data.issueAging[age.key]), 0);
  const old = count(data.issueAging.over14d);
  const ageNote = agingTotal === 0 ? (total === 0 ? 'No issues yet.' : 'No active issues to age.')
    : old === agingTotal ? (old === 1 ? 'The active issue is 14d+ old.' : `All ${old} active issues are 14d+ old.`)
    : old > 0 ? `${old} of ${agingTotal} active issues ${old === 1 ? 'is' : 'are'} 14d+ old.` : 'All active issues are under 14 days old.';
  const typeRows = ISSUE_TYPES
    .filter(type => type.pickable || count(data.typeBreakdown[type.value]) > 0)
    .map(type => {
      const visual = getTypeVisual(type.value);
      const icon = visual?.icon
        ? <HugeiconsIcon icon={visual.icon} size={13} strokeWidth={2} color="currentColor" className="shrink-0" aria-hidden="true" />
        : visual?.iconifyIcon ? <IconifyGlyph name={visual.iconifyIcon} className="w-[13px] h-[13px] shrink-0" /> : null;
      return { key: type.value, label: type.shortLabel, value: count(data.typeBreakdown[type.value]), icon, color: 'var(--color-primary)' };
    }).sort((a, b) => b.value - a.value);
  const allScope = `${total > 0 ? 'All ' : ''}${issues(total)}`;
  const panels: Record<Breakdown, { scope: string; rows: StatBarDatum[] }> = {
    severity: { scope: `${allScope} · highest severity first`, rows: SEVERITIES.map(item => ({ ...item, value: count(data.severityBreakdown[item.key]), color: `var(--color-severity-chart-${item.key})` })) },
    type: { scope: `${allScope} · most common first`, rows: typeRows },
    age: { scope: `${agingTotal} active ${agingTotal === 1 ? 'issue' : 'issues'} · age since capture`, rows: AGES.map(item => ({ ...item, value: count(data.issueAging[item.key]), color: 'var(--color-primary)' })) },
  };

  return <section className="project-overview card-sm mb-7" aria-label="Project overview">
    <div className="project-overview-top">
      <h2 className="text-sm font-semibold text-gray-700">Project overview</h2>
      <p className="text-xs text-gray-500">{total} total {total === 1 ? 'issue' : 'issues'} <span className="px-1" aria-hidden="true">·</span> Ignored issues excluded</p>
    </div>
    <div className="project-overview-grid">
      <div className="project-overview-progress">
        <div className="project-overview-progress-heading">
          <p className="project-overview-active text-gray-500"><strong className="text-gray-900 tabular-nums">{active}</strong><span>active {active === 1 ? 'issue' : 'issues'}</span></p>
          <p className="flex items-center gap-2 text-xs text-gray-500"><span className="w-1.5 h-1.5 rounded-full bg-[var(--color-status-chart-resolved)]" aria-hidden="true" />{count(data.statusBreakdown.resolved)} resolved</p>
        </div>
        <div className="project-overview-distribution bg-gray-100" role="img" aria-label={`${issues(total)}: ${statuses.map(status => `${status.value} ${status.label.toLowerCase()}`).join(', ')}`}>
          {statuses.filter(status => status.value > 0).map(status => <span key={status.key} style={{ width: `${status.value / total * 100}%`, backgroundColor: `var(--color-status-chart-${status.key})` }} />)}
        </div>
        <dl className="project-overview-statuses">
          {statuses.map(status => <div key={status.key}>
            <dt className="flex items-center gap-2"><span className="w-[5px] h-[5px] rounded-full" style={{ backgroundColor: `var(--color-status-chart-${status.key})` }} aria-hidden="true" />{status.label}</dt>
            <dd className="font-semibold text-gray-700 tabular-nums">{status.value}</dd>
          </div>)}
        </dl>
        <div className="project-overview-age-note text-xs text-gray-500">
          <HugeiconsIcon icon={Clock01Icon} size={14} strokeWidth={2} color="currentColor" className="shrink-0" aria-hidden="true" />
          <p>{ageNote}</p>
          <button type="button" className="project-overview-age-link text-primary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2" onClick={() => {
            setBreakdown('age');
            document.getElementById(`${id}-age-tab`)?.focus();
            document.getElementById(`${id}-age-tab`)?.scrollIntoView({ block: 'nearest' });
          }}><span className="text-cap-trim">View ages</span> <HugeiconsIcon icon={ArrowUpRight01Icon} size={12} strokeWidth={2} color="currentColor" aria-hidden="true" /></button>
        </div>
      </div>
      <div className="project-overview-breakdown">
        <SegmentedControl size="sm" idPrefix={id} aria-label="Issue breakdown" className="w-fit" value={breakdown} onChange={setBreakdown} options={TABS} />
        {/* Panels share one grid cell, reserving the tallest real panel's space
            so switching tabs never shifts the issue list. Hidden panels are
            invisible to assistive technology and cannot receive focus. */}
        <div className="project-overview-panels">
          {TABS.map(tab => <div key={tab.value} id={`${id}-${tab.value}-panel`} className="project-overview-panel" role="tabpanel" aria-labelledby={`${id}-${tab.value}-tab`} aria-hidden={breakdown !== tab.value} tabIndex={breakdown === tab.value ? 0 : -1}>
            <p className="mt-3 mb-4 text-xs text-gray-500">{panels[tab.value].scope}</p>
            <StatBars variant="compact" rows={panels[tab.value].rows} compactLabelWidth={tab.value === 'age' ? 125 : 105} className={tab.value === 'severity' ? 'gap-4' : tab.value === 'type' ? 'gap-3' : 'gap-3'} />
          </div>)}
        </div>
      </div>
    </div>
  </section>;
}

export function ProjectOverviewSkeleton() {
  return <section className="project-overview card-sm mb-7" aria-label="Project overview loading" aria-busy="true">
    <div className="project-overview-top"><div className="h-4 w-32 rounded skeleton-shimmer" /></div>
    <div className="project-overview-grid" aria-hidden="true">
      <div className="project-overview-progress space-y-6"><div className="h-11 w-40 rounded skeleton-shimmer" /><div className="h-3 rounded skeleton-shimmer" /><div className="h-4 w-3/4 rounded skeleton-shimmer" /><div className="h-4 rounded skeleton-shimmer" /></div>
      <div className="project-overview-breakdown space-y-4"><div className="h-7 w-48 rounded skeleton-shimmer" />{SEVERITIES.map(item => <div key={item.key} className="h-4 rounded skeleton-shimmer" />)}</div>
    </div>
  </section>;
}
